/*
 * *****************************************************************************
 *  Copyright (C)  Motorola Solutions, INC.
 *  All Rights Reserved.
 *  Motorola Solutions Confidential Restricted.
 *  ******************************************************************************
 */
import * as uuid from 'uuid';
import { Injectable, OnDestroy } from '@angular/core';
import { debounceTime, filter, Subscription } from 'rxjs';
import { Store } from '@ngrx/store';
import { selectClusterAddressMapWithState } from '../../call/+state/media.selectors';
import { IMessage, RxStompState } from '@stomp/rx-stomp';
import { delay } from 'rxjs/operators';
import { Topic } from '../../user/model/multiple-subscriptions';
import { StompManagerService } from '../services/stomp-manager/stomp-manager.service';

@Injectable({
    providedIn: 'root'
})
export abstract class StompBaseConsumer implements OnDestroy {
    private topicSubscriptions: { [clusterName: string]: { [topic: string]: Subscription } } = {};
    private subscribedClusters: Set<string> = new Set();

    abstract handleSubscriptionInit(topic: string, receiptId: string, clusterName?: string, clusterLabel?: string): void;

    abstract handleSubscriptionEnd(topic: string, receiptId: string, clusterName?: string, clusterLabel?: string): void;

    abstract handleStompError(topic: string, receiptId: string, error: string, clusterName?: string, clusterLabel?: string): void;

    protected abstract getTopics(): Topic[];

    protected abstract getTopicHandlers(): { [topic in Topic]?: (message: any, clusterName?: string) => void };

    protected getTopicDelays(): { [topic: string]: number } {
        return {};
    }

    protected constructor(
        protected stompManager: StompManagerService,
        protected store: Store
    ) {
            this.store.select(selectClusterAddressMapWithState)
                .pipe(
                    filter((clustersMap) => !!clustersMap.length),
                    debounceTime(250)
                )
                .subscribe((clustersMap) => {
                    clustersMap.forEach(({ name, label, state }) => {
                        if (state !== RxStompState.OPEN || this.subscribedClusters.has(name)) {
                            return;
                        }
                        this.subscribeToCluster(name, label);
                    });
                });
    }

    private subscribeToCluster(clusterName: string, clusterLabel: string): void {
        if (this.subscribedClusters.has(clusterName)) {
            console.warn(`${clusterLabel} is already subscribed.`);
            return;
        }
        const topics = this.getTopics();
        const topicDelays = this.getTopicDelays();
        this.topicSubscriptions[clusterName] = {};

        topics.forEach((topic) => {
            const receipt = uuid.v4();
            const delayTime = topicDelays[topic] || 0;
            this.topicSubscriptions[clusterName][topic] = this.stompManager.watchAsync(topic, receipt, this, clusterName, clusterLabel)
                .pipe(delay(delayTime))
                .subscribe((message: IMessage) => {
                    this.handleMessage(message, topic, clusterName);
                });
        });

        this.subscribedClusters.add(clusterName);
    }

    private handleMessage(message: IMessage, topic: Topic, clusterName: string): void {
        const handler = this.getTopicHandlers()[topic];
        if (handler) {
            handler(JSON.parse(message.body), clusterName);
        } else {
            console.warn(`No handler found for topic: ${topic}`);
        }
    }

    ngOnDestroy(): void {
        Object.values(this.topicSubscriptions).forEach((clusterSubscriptions) => {
            Object.values(clusterSubscriptions).forEach((subscription) => subscription.unsubscribe());
        });
        this.topicSubscriptions = {};
        this.subscribedClusters.clear();
    }
}
